import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { CancelTokenSource } from 'axios';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { SchedulingView, TimeDisplayMode } from './SchedulingTypes';
import { encryptData } from 'services/authentication/encryptData';
import { shakeElement } from 'services/utils/shakeElement';
import { saveData } from 'services/api/saveData';
import FormFieldContext from 'components/forms/FormFieldContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import TabsField from 'components/forms/fields/TabsField';
import SwitchButton from 'components/buttons/SwitchButton';
import '../../style/scss/forms.scss';
import '../../style/scss/modals.scss';
import '../../style/scss/settings.scss';

const CustomizeSchedulingModal: React.FC<{
    viewKey?: string;
    view: SchedulingView;
    onViewChange: (view: SchedulingView) => void;
    onSave?: (updatedData: Record<string, any>) => Promise<boolean>;
    headerKey?: string;
    explanationKey?: string;
}> = ({ 
    viewKey, view, onViewChange, onSave, headerKey, explanationKey
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { userHash, activeFeatures } = useAuthContext();
    const { setUserPreferences } = useSettings();
    const { modalStack, revealModal, closeModal } = useModal();
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [buttonLoader, setButtonLoader] = useState(false);
    const [cancelSource, setCancelSource] = useState<CancelTokenSource | null>(null);
    const topModal = modalStack[modalStack.length - 1];

    // Reveal the modal on mount
    useEffect(() => revealModal(), []);

    // Update the changes to the view
    const updateSettingsToView = (
        currentView: SchedulingView, 
        updatedData: Record<string, any>
    ): SchedulingView => {
        // Initialize the updated view with the current view data
        const updatedView = { ...currentView };

        // Update the scheduling view
        if (updatedData.scheduling_view != null) {
            updatedView.viewType = updatedData.scheduling_view;
        }

        // Update the default period
        if (updatedData.scheduling_default_view_period != null) {
            updatedView.period = updatedData.scheduling_default_view_period;
        }

        // Update the scheduling direction
        if (updatedData.scheduling_direction != null) {
            updatedView.direction = updatedData.scheduling_direction;
        }

        // Update the days to show
        if (updatedData.scheduling_display_mode != null) {
            updatedView.showDays = updatedData.scheduling_display_mode;
        }

        // Update the color mode
        if (updatedData.scheduling_color_mode != null) {
            updatedView.colorMode = updatedData.scheduling_color_mode;
        }

        // Update the automatic open job inbox state, false is a valid value
        if (updatedData.scheduling_auto_open_job_inbox != null) {
            updatedView.showJobInbox = updatedData.scheduling_auto_open_job_inbox;
        }

        // Update the show profile pictures state, false is a valid value
        if (updatedData.scheduling_show_profile_pictures != null) {
            updatedView.showProfilePictures = updatedData.scheduling_show_profile_pictures;
        }

        // Determine and update the time display mode
        const viewType = updatedData.scheduling_view ?? currentView.viewType;
        const hasTimeBlocks = updatedData.scheduling_time_blocks ?? currentView.renderTimeBlocks;

        // Initialize to none, render timeslots or timeblocks
        let timeDisplayMode: TimeDisplayMode = "none";
        if (viewType === "employee_timeline") {
            timeDisplayMode = "timeslots";
        } else if (viewType === "employee_list" && hasTimeBlocks) {
            timeDisplayMode = "timeblocks";
        }

        // Set the time display mode
        updatedView.timeDisplayMode = timeDisplayMode;

        return updatedView;
    };

    // Handle submit
    const handleSubmit = async () => {
        try {
            // Start the button loader
            setButtonLoader(true);

            // Hand the chosen settings to the custom save action when given
            if (onSave) {
                const saved = await onSave(updatedData);
                if (saved) {
                    handleClose();
                } else {
                    setButtonLoader(false);
                    if (topModal?.modalRef) shakeElement(topModal.modalRef.current as HTMLElement);
                }
                return;
            }

            // Create the cancel token source
            const source = axios.CancelToken.source();
            setCancelSource(source);
                        
            // Submit to backend
            const response = await saveData({ 
                apiUrl: `patch_user_preference/${userHash}`,
                method: 'patch',
                data: updatedData,
                source
            })

            // Handle succesful response
            if (response?.status === 200) {

                // Update the user preferences state
                setUserPreferences(response.data);

                // Convert the user preferences to a string and store it encrypted in the local storage
                const preferencesString = JSON.stringify(response.data);
                localStorage.setItem('user_preferences', encryptData(preferencesString))

                // Update the scheduling view
                const newView = updateSettingsToView(view, updatedData);
                onViewChange(newView);

                // Show success alert
                setFloatingAlert({ type: 'success' })

                // Close the modal
                handleClose();
            }
        } catch (error) {
            setButtonLoader(false);

            // Shake the modal in any case of an error
            if (topModal?.modalRef) shakeElement(topModal.modalRef.current as HTMLElement);
        }
    };

    // Closes the modal when clicking the close or cancel button
    const handleClose = () => {
        setUpdatedData({});
        closeModal();

        // If a request is still pending when closing, cancel the request
        if (cancelSource) cancelSource.cancel();

        // Stops the button loader icon
        setButtonLoader(false);
    };

    if (!viewKey) return null;

    return (
        <FormFieldContext.Provider value={{ updatedData, setUpdatedData }}>
            <div className='customize-scheduling-modal'>
                <h3 className='header text-center'
                    style={{ marginBottom: '1rem' }}>
                        {t(headerKey ?? 'scheduling.customize_modal.header')}
                </h3>
                {/* Show the explanation of the custom save action when given */}
                {explanationKey && (
                    <p className='explanation-text text-center'
                        style={{ marginBottom: '1rem' }}>
                            {t(explanationKey)}
                    </p>
                )}
                <div className='settings-form'>
                    {/* Only show the view choice when the scheduling feature offers other views */}
                    {activeFeatures.includes('scheduling') && (
                        <div className='setting-item'>
                            <div>
                                <div className='title'>
                                    <h5>{t('scheduling.customize_modal.view_title')}</h5>
                                </div>
                                <div className='content'>
                                    <TabsField
                                        viewKey={viewKey}
                                        type="tabs"
                                        name="scheduling_view"
                                        defaultValue={view.viewType}
                                        buttonSize='large'
                                        options={[
                                            { value: 'personal_calendar', name: 'scheduling.settings.calendar_option' },
                                            { value: 'employee_list', name: 'scheduling.settings.list_view_option', requiredFeature: 'scheduling_timeline_views' },
                                            { value: 'employee_timeline', name: 'scheduling.settings.timeline_view_option', requiredFeature: 'scheduling_timeline_views' },
                                            { value: 'project_planner', name: 'scheduling.settings.project_planner_option', requiredFeature: 'project_planner' }
                                        ]}
                                        viewInEditMode={true}
                                    />
                                    <p className='explanation-text'>
                                        {t('scheduling.customize_modal.view_explanation')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    {((updatedData.scheduling_view ?? view.viewType) === 'employee_list' || (updatedData.scheduling_view ?? view.viewType) === 'employee_timeline') && (
                        <div className='setting-item'>
                            <div>
                                <div className='title'>
                                    <h5>{t('scheduling.settings.calendar_direction_title')}</h5>
                                </div>
                                <div className='content'>
                                    <TabsField
                                        viewKey={viewKey}
                                        type="tabs"
                                        name="scheduling_direction"
                                        defaultValue={view.direction}
                                        buttonSize='large'
                                        options={[
                                            { value: 'stacked', name: 'scheduling.settings.stacked_option' },
                                            { value: 'columns', name: 'scheduling.settings.columns_option' }
                                        ]}
                                        viewInEditMode={true}
                                    />
                                    <p className='explanation-text'>
                                        {t('scheduling.settings.calendar_direction_explanation')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    {((updatedData.scheduling_view ?? view.viewType) === 'employee_list' || (updatedData.scheduling_view ?? view.viewType) === 'employee_timeline') && (
                        <div className='setting-item'>
                            <div>
                                <div className='title'>
                                    <h5>{t('scheduling.settings.default_period_title')}</h5>
                                </div>
                                <div className='content'>
                                    <TabsField
                                        viewKey={viewKey}
                                        type="tabs"
                                        name="scheduling_default_view_period"
                                        defaultValue={view.period}
                                        buttonSize='large'
                                        options={[
                                            { value: 'day', name: 'scheduling.settings.day_option' },
                                            { value: 'week', name: 'scheduling.settings.week_option' },
                                        ]}
                                        viewInEditMode={true}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {((updatedData.scheduling_view ?? view.viewType) === 'personal_calendar' || (updatedData.scheduling_view ?? view.viewType) === 'employee_list' || (updatedData.scheduling_view ?? view.viewType) === 'employee_timeline') && (
                         (
                            <div className='setting-item'>
                                <div>
                                    <div className='title'>
                                        <h5>{t('scheduling.settings.displayed_days_title')}</h5>
                                    </div>
                                    <div className='content'>
                                        <TabsField
                                            viewKey={viewKey}
                                            type="tabs"
                                            name="scheduling_display_mode"
                                            defaultValue={view.showDays}
                                            buttonSize='large'
                                            options={[
                                                { value: 'mon-fri', name: 'scheduling.settings.mon_to_fri_option' },
                                                { value: 'mon-sat', name: 'scheduling.settings.mon_to_sat_option' },
                                                { value: 'all', name: 'scheduling.settings.all_days_option' },
                                            ]}
                                            viewInEditMode={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                    <div className='setting-item'>
                        <div>
                            <div className='title'>
                                <h5>{t('scheduling.settings.appointment_colors_title')}</h5>
                            </div>
                            <div className='content'>
                                <TabsField
                                    viewKey={viewKey}
                                    type="tabs"
                                    name="scheduling_color_mode"
                                    defaultValue={view.colorMode}
                                    buttonSize='large'
                                    options={[
                                        { value: 'status', name: 'scheduling.settings.job_status_option' },
                                        { value: 'type', name: 'scheduling.settings.job_type_option', requiredFeature: 'job_types' },
                                        { value: 'custom', name: 'scheduling.settings.custom_option' },
                                    ]}
                                    viewInEditMode={true}
                                />
                            </div>
                        </div>
                    </div>
                    {((updatedData.scheduling_view ?? view.viewType) === 'employee_list' || (updatedData.scheduling_view ?? view.viewType) === 'employee_timeline') && (
                        <>
                            {activeFeatures.includes('job_inbox') && (
                                <div className='setting-item'>
                                    <div className='switch-button-wrapper'>
                                        <SwitchButton
                                            name='scheduling_auto_open_job_inbox'
                                            value={updatedData.scheduling_auto_open_job_inbox ?? view.showJobInbox}
                                            onToggle={(isOn) => setUpdatedData(prev => ({ ...prev, scheduling_auto_open_job_inbox: isOn }) )}
                                        />
                                    </div>
                                    <div>
                                        <div className='title switch'>
                                            <h5>{t('scheduling.customize_modal.auto_open_job_inbox_title')}</h5>
                                        </div>
                                        <div className='content'>
                                            <p className='explanation-text'>
                                                {t('scheduling.customize_modal.auto_open_job_inbox_explanation')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className='setting-item'>
                                <div className='switch-button-wrapper'>
                                    <SwitchButton
                                        name='scheduling_show_profile_pictures'
                                        value={updatedData.scheduling_show_profile_pictures ?? view.showProfilePictures}
                                        onToggle={(isOn) => setUpdatedData(prev => ({ ...prev, scheduling_show_profile_pictures: isOn }) )}
                                    />
                                </div>
                                <div>
                                    <div className='title switch'>
                                        <h5>{t('scheduling.settings.show_profile_pictures_title')}</h5>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <div className='footer'>
                    <div className='buttons-left'>
                    </div>
                    <div className='buttons-right'>
                        <SecondaryButton
                            onClick={closeModal} 
                            label="general.cancel"
                            size="small"/>
                        <PrimaryButton
                            onClick={handleSubmit}
                            label="general.save"
                            size="small"
                            loading={buttonLoader}/>
                    </div>
                </div>
            </div>
        </FormFieldContext.Provider>
    );
};

export default CustomizeSchedulingModal;